import { Box, Stack, Typography } from "@mui/material";
import { isEmpty, isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AppIconButton from "@/common/components/Button/AppIconButton";
import Datetime from "@/common/components/Datetime/Datetime";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import GeneralStageHistory from "@/common/components/GeneralStageHistory";
import AppIcon from "@/common/components/Icons/AppIcon";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import GeneralPriceSummaryDisplay from "@/common/components/PriceSummary/GeneralPriceSummaryDisplay";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, EntityType, MaintenanceDto } from "@/core/api/generated/v1";

import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralByWhoDisplay from "../../../General/Display/GeneralByWhoDisplay";
import SupplierLink from "../../../Supplier/SupplierLink";
import VehicleLink from "../../../Vehicle/VehicleLink";

interface Props {
  maintenance: MaintenanceDto;
  onUpdate: (newValue?: MaintenanceDto) => void;
}

export default function OverviewTabContent({ maintenance, onUpdate }: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const { hasPermissions } = useAuthorizationInfo();

  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);
  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={maintenance} />}
          />
        }
      >
        <ViewContentBlock>
          <Stack gap={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(maintenance?.localNumber)}
            >
              {maintenance?.localNumber}
            </InfoTile> */}

              <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(maintenance?.vehicle)}>
                <VehicleLink entity={maintenance?.vehicle} />
              </InfoTile>

              {/* <InfoTile label='Contract' iconOf='contract' isEmpty={isNil(maintenance?.contract)}>
                <ContractLink entity={maintenance?.contract} />
              </InfoTile> */}

              <InfoTile label='Supplier' iconOf='supplier' isEmpty={isNil(maintenance?.supplier)}>
                <SupplierLink entity={maintenance?.supplier} />
              </InfoTile>

              <InfoTile
                label='External number'
                iconOf='externalNumber'
                isEmpty={!maintenance.externalNumber}
                endAdornment={
                  <BaseEditExternalNumberModal
                    initialValues={{ externalNumber: maintenance.externalNumber }}
                    onConfirm={async (externalNumber) => {
                      await apiClient.maintenancesApi.apiV1MaintenancesMaintenanceIdExternalNumberPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          maintenanceId: maintenance.id || "",
                          generalExternalNumberInputDto: { externalNumber },
                        },
                      );
                      onUpdate();
                    }}
                  />
                }
              >
                {maintenance.externalNumber}
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile label='Name' iconOf='info' isEmpty={isNil(maintenance?.name)}>
                {maintenance?.name}
              </InfoTile>

              <InfoTile
                label='Schedule type'
                iconOf='maintenanceScheduleType'
                isEmpty={isNil(maintenance?.scheduleType)}
              >
                <InlineApiEnumValue
                  type='MaintenanceScheduleType'
                  value={maintenance.scheduleType}
                />
              </InfoTile>

              <InfoTile
                label='Scheduled At'
                iconOf='date'
                isEmpty={isNil(maintenance?.scheduledAt)}
              >
                <Datetime datetime={maintenance?.scheduledAt} />
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile
                label='Mileage'
                iconOf='gauge'
                isEmpty={!maintenance?.mileage}
                endAdornment={
                  <BaseEditMileageModal
                    vehicleId={maintenance.vehicle?.id}
                    mileage={maintenance.mileage}
                    trigger={
                      <AppIconButton
                        sx={{ p: "2px", ml: 0.5 }}
                        onClick={() => setIsUpdateMileageModalOpened(true)}
                      >
                        <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                      </AppIconButton>
                    }
                    triggerParams={{ isDisableDefaultAction: true }}
                    open={isUpdateMileageModalOpened}
                    onClose={() => setIsUpdateMileageModalOpened(false)}
                    onConfirm={async (mileage) => {
                      await apiClient.maintenancesApi.apiV1MaintenancesMaintenanceIdMileagePut({
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        maintenanceId: maintenance.id || "",
                        updateVehicleMileageDto: {
                          vehicleId: maintenance.vehicle?.id,
                          mileage: mileage,
                        },
                      });
                    }}
                    onMileageChanged={() => onUpdate()}
                  />
                }
              >
                {maintenance.mileage?.mileage} {maintenance.mileage?.unit}
              </InfoTile>

              <InfoTile label='Fuel level' iconOf='fuel' isEmpty={isNil(maintenance?.fuelLevel)}>
                {maintenance?.fuelLevel}
              </InfoTile>

              <InfoTile
                label='Responsible user'
                iconOf='inspector'
                isEmpty={isNil(maintenance?.responsibleUser)}
              >
                <GeneralByWhoDisplay who={maintenance.responsibleUser} />
              </InfoTile>

              <InfoTile
                label='Notes'
                iconOf='notes'
                isEmpty={!maintenance.notes}
                endAdornment={
                  <BaseEditNotesModal
                    initialValues={{ notes: maintenance?.notes }}
                    onConfirm={async (notes) => {
                      await apiClient.maintenancesApi.apiV1MaintenancesMaintenanceIdNotesPut({
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        maintenanceId: maintenance.id || "",
                        generalNotesInputDto: { notes },
                      });
                      onUpdate();
                    }}
                  />
                }
              >
                {maintenance.notes}
              </InfoTile>
            </Box>
          </Stack>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.Maintenance}
          entityId={maintenance.id}
          entity={maintenance}
          entityUpdatePermission={AppPermission.MaintenanceManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {/* Stages */}
        <ViewContentBlock>
          <Typography variant='h2'>Stages</Typography>
          <GeneralStageHistory
            stageEnumType='MaintenanceStage'
            stageHistory={maintenance.stageHistory}
            withUpdateButtons={hasPermissions([AppPermission.MaintenanceManage])}
            onUpdateStage={async (newStage) => {
              try {
                const response =
                  await apiClient.maintenancesApi.apiV1MaintenancesMaintenanceIdStagePut({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    maintenanceId: maintenance.id || "",
                    generalStageUpdateDtoOfMaintenanceStage: {
                      newStage: newStage,
                    },
                  });
                onUpdate(response.data);
              } catch (err) {
                const validation2 = ValidationHelper.handleApiErrorResponse(err);
                enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
              }
            }}
          />
        </ViewContentBlock>

        {!isEmpty(maintenance?.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(maintenance?.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {/* Summary */}
        <ViewContentBlock alignSelf='flex-end'>
          <GeneralPriceSummaryDisplay
            sx={{ minWidth: { xxs: "100%", md: "300px" } }}
            summary={{
              currency: maintenance.currency,
              subTotal: maintenance.subTotal,
              subTotalIncDiscount: maintenance.subTotalIncDiscount,
              discount: maintenance.discount,
              tax: maintenance.tax,
              total: maintenance.total,
            }}
          />
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
